/**
 * 恒星管控（纯逻辑模块）。
 *
 * 口径（2026-10-10 确认）：
 *   1. 名单见 `../config/starControlStars.js`，**数量不固定**，注释掉的条目不会生效；
 *   2. 名单内恒星**恒参与**星盘请求（selectedStars 并集），用户无法通过 UI 取消；
 *   3. 名单内恒星**默认不上画布**，但照常参与计算与点/面报警判定；
 *   4. 配置弹窗里的按钮/容许度行**默认屏蔽**，需开通「恒星管控」权限才展示。
 *
 * 本模块不依赖 window / axios / Vue，便于 node 直跑断言。
 */
const STAR_CONTROL_STARS = require('../config/starControlStars')
const purchaseCatalog = require('./permissionPurchaseCatalog')

/** 权限目录里的 key / users 到期字段（与 permissionPurchaseCatalog、后端 PermissionCatalog 对齐） */
const STAR_CONTROL_KEY = 'star_control'
const STAR_CONTROL_ENTITLEMENT_FIELD = 'starControl'

/** 规范化编号：可接受 number / 数字字符串；非法值返回 null */
function normalizeStarNum (value) {
  if (value === undefined || value === null || value === '') {
    return null
  }
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

/** 名单副本（避免调用方原地改到常量） */
function getControlledStars () {
  return STAR_CONTROL_STARS.map(item => ({ ...item }))
}

/** 名单内的编号集合（number） */
function getControlledStarNums () {
  return STAR_CONTROL_STARS
    .map(item => normalizeStarNum(item.starNum))
    .filter(num => num !== null)
}

/** 是否为受管控恒星（按 starNum / id 判定） */
function isControlledStar (starNumOrId) {
  const num = normalizeStarNum(starNumOrId)
  if (num === null) {
    return false
  }
  return getControlledStarNums().includes(num)
}

/**
 * 把受管控恒星并入已选列表（并集 + 去重 + 升序）。
 *
 * 这是「用户怎么点都去不掉」的关键收口：恒星配置弹窗、读取已存配置、
 * 组装请求参数三处都要经过它。
 *
 * @param {Array<number|string>} starIds 当前已选恒星编号
 * @returns {Array<number>} 含受管控恒星的编号列表（保持数字类型，便于 === 比较）
 */
function withControlledStars (starIds) {
  const merged = new Set()
  ;(Array.isArray(starIds) ? starIds : []).forEach(id => {
    const num = normalizeStarNum(id)
    if (num !== null) {
      merged.add(num)
    }
  })
  getControlledStarNums().forEach(num => merged.add(num))
  return Array.from(merged).sort((a, b) => a - b)
}

/**
 * 名单内的恒星是否应从「恒星配置」弹窗中隐藏。
 * @param {Object} star 恒星对象（取 id）
 * @param {Object} user 登录态 user（localStorage.user）
 */
function shouldHideControlledStar (star, user) {
  if (!isControlledStar(star && star.id)) {
    return false
  }
  return !isStarControlGranted(user)
}

/**
 * 当前账号是否已开通「恒星管控」。
 * 与点报警/面报警同一通道：读登录态里的到期时间字段（users.starControl）。
 */
function isStarControlGranted (user) {
  if (!user || typeof user !== 'object') {
    return false
  }
  return purchaseCatalog.isEntitlementActive(user[STAR_CONTROL_ENTITLEMENT_FIELD])
}

/** 读取本地登录态（口径与 services/userContext、utils/wechatPay 一致） */
function readCurrentUser () {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem('user')
    return raw ? JSON.parse(raw) : null
  } catch (error) {
    return null
  }
}

/** 按本地登录态直接判断恒星管控是否已开通（弹窗渲染用） */
function isStarControlGrantedFromStorage () {
  return isStarControlGranted(readCurrentUser())
}

module.exports = {
  STAR_CONTROL_KEY,
  STAR_CONTROL_ENTITLEMENT_FIELD,
  getControlledStars,
  getControlledStarNums,
  isControlledStar,
  withControlledStars,
  shouldHideControlledStar,
  isStarControlGranted,
  readCurrentUser,
  isStarControlGrantedFromStorage
}
